<script>
  import { Alert, AuthProviderButton } from '@storytel/design-system-web';
  import { onMount, tick } from 'svelte';
  import { getFirebaseConfig } from '../config';
  import ProviderCentered from '../styling/ProviderCentered.svelte';
  import { initializeApp, getApps } from 'firebase/app';
  import { l10n } from '../utils/Utils';
  import { createEventDispatcher } from 'svelte';
  import SthpFacebookLogin from './SthpFacebookLogin.svelte';
  import { exchangeIdTokenForSsoToken } from '../services/SthpService';

  const dispatch = createEventDispatcher();

  import {
    GoogleAuthProvider,
    OAuthProvider,
    getAuth,
    getRedirectResult,
    signInWithRedirect,
  } from 'firebase/auth';

  export let env, providerInfo, showLoader, country, language, currentErrorOrigin;

  let appleProvider = new OAuthProvider('apple.com'),
    isMounted = false,
    auth,
    showError = false,
    errorText = '';

  onMount(async () => {
    const firebaseConfig = getFirebaseConfig(env, country);
    if (getApps().length === 0) {
      const app = initializeApp(firebaseConfig);
      auth = getAuth(app);
      getRedirectResult(auth).then(redirectResult);
    }

    await tick();
    isMounted = true;
  });

  const handleFirebaseSuccess = async (firebaseResult) => {
    const providerId = firebaseResult.providerId ?? '';

    if (firebaseResult.user) {
      try {
        const idToken = await firebaseResult.user.getIdToken();
        const result = await exchangeIdTokenForSsoToken(idToken, env, country, language);

        if (result.result === 'error') {
          if (result.code.indexOf('404') !== -1) {
            if (providerId === 'apple.com') {
              return {
                error: true,
                errorText: l10n('apple.login.error.noAccount'),
              };
            } else if (providerId === 'google.com') {
              return {
                error: true,
                errorText: l10n('google.login.error.noAccount'),
              };
            }
            throw `User has invalid providerId ${providerId}`;
          }
          throw `Exchange token failed with error code ${result.code}`;
        }

        await auth.signOut();
        console.log('Handle Firebase Success');
        return {
          error: false,
          errorText: '',
          providerId,
        };
      } catch (error) {
        console.error('Error on sign-in', error);
        return {
          error: true,
          errorText: l10n('generic.error.contactSupport'),
          providerId,
        };
      }
    }
  };

  const redirectResult = async (firebaseResult) => {
    try {
      if (firebaseResult && firebaseResult.user) {
        showLoader = true;
        const result = await handleFirebaseSuccess(firebaseResult);

        if (result.error) {
          showAlert(result.errorText);
        } else {
          dispatch('loginSuccess', { method: result.providerId });
        }
      }
    } catch (error) {
      showAlert(l10n('generic.error.contactSupport'));
    }
  };

  const login = async (provider) => {
    dispatch('error', { component: '' });

    await signInWithRedirect(auth, provider);
  };

  const showAlert = (text) => {
    dispatch('error', { component: 'ProviderLogin' });
    showLoader = false;
    showError = true;
    errorText = text;
  };

</script>

{#if isMounted}
  {#if providerInfo.isGoogleEnabled}
    <ProviderCentered testId={'google-provider-button'}>
      <AuthProviderButton fullwidth on:click={() => login(new GoogleAuthProvider())} type="google">
        {l10n('google.loginWith')}
      </AuthProviderButton>
    </ProviderCentered>
  {/if}

  {#if providerInfo.isAppleEnabled}
    <ProviderCentered testId={'apple-provider-button'}>
      <AuthProviderButton fullwidth on:click={() => login(appleProvider)} type="apple">
        {l10n('apple.loginWith')}
      </AuthProviderButton>
    </ProviderCentered>
  {/if}

  {#if providerInfo.isFacebookEnabled}
    <ProviderCentered testId={'facebook-provider-button'}>
      <SthpFacebookLogin
        {env}
        on:loginSuccess={() => dispatch('loginSuccess', { method: 'facebook' })}
        on:loginError={(event) => showAlert(event.detail)}
      />
    </ProviderCentered>
  {/if}

  {#if currentErrorOrigin === 'ProviderLogin' && showError}
    <Alert type="error">{errorText}</Alert>
  {/if}
{/if}
